import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Formik } from 'formik';
import { useModal } from 'mui-modal-provider';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { nanoid } from 'nanoid';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { TextEmphasis } from '../components';
import { useActions, useAppState } from '../overmind';
import type { Schema, SchemaMappingType } from '../types';
import { type EditSchemaDialogProps } from './type';

interface SchemaForm {
  name: string;
  mapping: SchemaMappingType;
  rng: string;
  css: string;
}

const defaultValue: SchemaForm = { name: '', mapping: 'tei', rng: '', css: '' };

export const EditSchemaDialog = ({
  actionType = 'add',
  docSchema,
  id = nanoid(),
  mappingIds = [],
  maxWidth = 'md',
  onAcceptChanges,
  onClose,
  onDelete,
  open = false,
  schemaId,
}: EditSchemaDialogProps) => {
  const { schemaId: documentSchemaId } = useAppState().document;
  const { schemas } = useAppState().editor;

  const { addSchema, deleteSchema, updateSchema } = useActions().editor;
  const { closeDialog, notifyViaSnackbar, openDialog } = useActions().ui;

  const { destroyModal } = useModal();

  const { t } = useTranslation();

  const schema = schemaId ? schemas[schemaId] : undefined;

  const initVal: SchemaForm = schema
    ? {
        name: schema.name,
        mapping: schema.mapping,
        rng: schema.rng[0],
        css: schema.css[0],
      }
    : {
        ...defaultValue,
        rng: docSchema?.rng ?? '',
        css: docSchema?.css ?? '',
      };

  const [initialValues, setInitialValues] = useState<SchemaForm>(initVal);

  const preventEscape = actionType === 'add';

  const urlValidation = z
    .url({ message: t('LW.Must be a valid URL').toString() })
    .startsWith('https://', {
      message: t('LW.URL must start with HTTPS secured connection').toString(),
    });

  const formValidation = z
    .object({
      name: z
        .string({
          error: (issue) => (issue.input === undefined ? undefined : undefined),
        })
        .min(3, { message: t('LW.Must be at least characters', { min: 3 }).toString() })
        .max(20, {
          message: t('LW.Cannot have more than characters', { max: 20 }).toString(),
        }),
      mapping: z.string(),
      rng: urlValidation,
      css: urlValidation,
    })
    .partial({ css: true });

  const handleBeforeClose = () => {
    setInitialValues(defaultValue);
  };

  const handleClose = async (_event: MouseEvent, reason: string) => {
    if (preventEscape && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }

    handleBeforeClose();
    closeDialog(id);
    onClose && onClose(reason);
  };

  const handleCancel = () => {
    handleBeforeClose();
    closeDialog(id);
    onClose && onClose('cancel');
  };

  const handleDelete = async () => {
    if (!schemaId) return;
    const name = schemas[schemaId]?.name;
    if (!name) return;
    openDialog({
      type: 'simple',
      props: {
        maxWidth: 'xs',
        title: t('LW.deleteSchema').toString(),
        preventEscape: true,
        Body: () => (
          <Trans i18nKey="LW.messages.delete confirmation message" values={{ name }}>
            <Typography component="span">Are you sure you want to delete{` `}</Typography>
            <TextEmphasis color="info">{name}</TextEmphasis>
            <Typography component="span"> schema?</Typography>
          </Trans>
        ),
        actions: [
          { action: 'cancel', label: t('LW.commons.cancel').toString() },
          {
            action: 'delete',
            label: t('LW.commons.delete').toString(),
            variant: 'outlined',
          },
        ],
        onClose: async (action) => {
          if (action !== 'delete') return;

          destroyModal(id);
          handleCancel();

          await deleteSchema(schemaId);

          handleBeforeClose();
          onDelete && (await onDelete(schemaId));

          notifyViaSnackbar({
            message: t('LW.settings.schemas.messages.Schema deleted successfully'),
          });

          closeDialog(id);
          onClose && onClose('delete', schemaId);
        },
      },
    });
  };

  const submit = async ({ name, mapping, rng, css }: SchemaForm) => {
    const schemaToSubmit: Schema | Omit<Schema, 'id'> = {
      id: schemaId ?? undefined,
      name,
      mapping,
      rng: [rng],
      css: [css],
      editable: true,
    };

    const schema = schemaId
      ? await updateSchema(schemaToSubmit as Schema)
      : await addSchema(schemaToSubmit);
    if (!schema) return;

    if (schemaId) schema.id = schemaId;

    handleBeforeClose();

    await onAcceptChanges?.(schema);

    notifyViaSnackbar({ message: t('LW.settings.schemas.messages.Schema saved successfully') });

    closeDialog(id);
    onClose?.(actionType, schema);
  };

  return (
    <Dialog
      aria-labelledby="alert-dialog-title"
      disableAutoFocus
      disableEscapeKeyDown={preventEscape}
      fullWidth
      id={id}
      maxWidth={maxWidth}
      onClose={handleClose}
      open={open}
    >
      <DialogTitle id="form-dialog-title" sx={{ textTransform: 'capitalize' }} variant="h5">
        {actionType === 'add' ? t('LW.add schema') : t('LW.Change Schema')}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={toFormikValidationSchema(formValidation)}
      >
        {({ dirty, errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Stack
                direction="row"
                alignItems="center"
                gap={2}
                justifyContent="space-between"
                mb={2}
              >
                <TextField
                  autoFocus
                  autoComplete="off"
                  error={Boolean(touched.name && errors.name)}
                  fullWidth
                  helperText={touched.name && errors.name ? errors.name : ' '}
                  label={t('LW.commons.Name')}
                  margin="dense"
                  name="name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  size="small"
                  sx={{ '::first-letter': { textTransform: 'uppercase' } }}
                  value={values.name}
                />
                {mappingIds.length > 1 && (
                  <Box width={250} mb={3}>
                    <TextField
                      fullWidth
                      label="Mapping"
                      margin="dense"
                      name="mapping"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      size="small"
                      select
                      slotProps={{
                        htmlInput: {
                          sx: { textTransform: 'uppercase' },
                        },
                      }}
                      sx={{ '::first-letter': { textTransform: 'uppercase' } }}
                      value={values.mapping}
                    >
                      {mappingIds.map((id) => (
                        <MenuItem key={id} sx={{ textTransform: 'uppercase' }} value={id}>
                          {id}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}
              </Stack>
              <TextField
                autoComplete="off"
                error={Boolean(touched.rng && errors.rng)}
                fullWidth
                helperText={touched.rng && errors.rng ? errors.rng : ' '}
                label="Schema URL"
                margin="dense"
                name="rng"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="https://"
                size="small"
                sx={{ '::first-letter': { textTransform: 'uppercase' } }}
                value={values.rng}
              />
              <TextField
                autoComplete="off"
                error={Boolean(touched.css && errors.css)}
                fullWidth
                helperText={touched.css && errors.css ? errors.css : ' '}
                label="CSS URL"
                margin="dense"
                name="css"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="https://"
                size="small"
                sx={{ '::first-letter': { textTransform: 'uppercase' } }}
                value={values.css}
              />
              {actionType === 'add' && (
                <Stack mt={2}>
                  <Typography variant="caption">
                    <span style={{ textTransform: 'uppercase', textDecoration: 'underline' }}>
                      {t('LW.note')}:
                    </span>
                    {` ${t(
                      'LW.LEAF-Writer cannot guarantee that the document will work correctly with the selected schema',
                    )}.
      ${t('LW.Tagging might not work as expected')}.`}
                  </Typography>
                </Stack>
              )}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Button onClick={handleCancel}>{t('LW.commons.cancel')}</Button>

              {schemaId && (
                <Tooltip
                  placement="top"
                  title={
                    schemaId === documentSchemaId
                      ? t('LW.The current document is using this schema')
                      : ''
                  }
                >
                  <span>
                    <Button
                      color="error"
                      disabled={schemaId === documentSchemaId}
                      onClick={handleDelete}
                    >
                      {t('LW.commons.delete')}
                    </Button>
                  </span>
                </Tooltip>
              )}

              <Button color="primary" disabled={!dirty} type="submit" variant="outlined">
                {schemaId ? t('LW.commons.update') : t('LW.commons.add')}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};
